import { useScanDate } from "@/components/ui/use-scan-date";
import { PREVIEW_CLOSE_DELAY_MS } from "@/data-preparation-tool/ui/scan-preview";
import { useImgPreviewSignedUrl } from "@/hooks/use-img-preview-signed-url";
import { useAnnotationIconColor } from "@/hooks/use-annotation-icon-color";
import { useCaptureAiDescription } from "@/hooks/use-capture-ai-description";
import { useCaptureAnnotationItems } from "@/hooks/use-capture-annotation-items";
import { useCaptureTreeIcon } from "@/hooks/use-capture-tree-icon";
import type { IElementWaypoint } from "@/hooks/use-placeholders";
import { useCached3DObjectIfReady } from "@/object-cache";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { isPanoObject } from "@/object-cache-type-guard";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { changeMode } from "@/store/mode-slice";
import { setFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { curryAppSelector } from "@/store/reselect";
import { setActiveElement } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { isIElementPhotogrammetryScan } from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import { selectIElementFloorPosition } from "@/utils/camera-transform";
import {
  isSingleScanSection,
  roundedCalculatePosition,
  selectAncestor,
  selectIElement,
  selectIElementWorldPosition,
} from "@faro-lotv/app-component-toolbox";
import {
  IElement,
  IElementImg2d,
  IElementImg360,
  IElementType,
  IElementTypeHint,
  isIElementGenericDataset,
  isIElementImg2d,
  isIElementImg360,
  isIElementPanoInOdometryPath,
  isIElementRoomSection,
  isIElementSection,
  isIElementTimeseries,
  isIElementTimeSeriesRoom,
  isIElementWithTypeAndHint,
} from "@faro-lotv/ielement-types";
import { IElementsState } from "@faro-lotv/project-source";
import { Box, useTheme } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { Vector3, Vector3Tuple } from "three";
import { AppAwareHtml } from "../renderers/app-aware-html";
import { PlaceholderPreviewBase } from "./placeholder-preview-base";

/** The offset that needs to be applies for popper from its anchor element in px */
const POPPER_OFFSET = 12;

/**
 * A memoized selector to resolve the reference element used to display a
 * waypoint's name and info for different types of scans (E57, Focus scan, Orbis).
 *
 * @param state The state of the app store
 * @param placeholder The waypoint placeholder to resolve the reference for
 * @returns The memoized selector
 */
const selectWaypointReference = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, placeholder: IElementWaypoint | undefined) =>
        placeholder,
    ],
    (
      iElements: IElementsState,
      placeholder: IElementWaypoint | undefined,
    ): IElement | undefined => {
      const state = { iElements };

      const refElementParent = selectIElement(placeholder?.parentId)(state);

      // Name to be displayed should be the section, ancestor of the time series
      const placeholderTimeSeries = selectAncestor(
        placeholder,
        isIElementTimeseries,
      )(state);
      const placeholderSection = selectAncestor(
        placeholderTimeSeries,
        isIElementSection,
      )(state);

      // Use the parent room section for name/date in case of 2D photo
      const parentRoomSection = selectAncestor(
        placeholder,
        isIElementRoomSection,
      )(state);

      // Keyframes in an odometry path reference their associated room section,
      // resolve it so the preview shows the room's info, not the keyframe's.
      const panoTargetRoomSection = selectIElement(
        placeholder && isIElementPanoInOdometryPath(placeholder)
          ? (placeholder.targetId ?? undefined)
          : undefined,
      )(state);
      const panoTargetTimeSeries = selectAncestor(
        panoTargetRoomSection,
        isIElementTimeseries,
      )(state);
      const panoTargetSection = selectAncestor(
        panoTargetTimeSeries,
        isIElementSection,
      )(state);

      if (
        refElementParent &&
        (isIElementWithTypeAndHint(
          refElementParent,
          IElementType.section,
          IElementTypeHint.structuredE57,
        ) ||
          isSingleScanSection(refElementParent))
      ) {
        return refElementParent;
      } else if (placeholder && isIElementImg2d(placeholder)) {
        return parentRoomSection;
      } else if (
        refElementParent &&
        isIElementWithTypeAndHint(
          refElementParent,
          IElementType.group,
          IElementTypeHint.rooms,
        )
      ) {
        // Element is an entire room. This applies for example to location markers.
        return placeholder;
      } else if (placeholder && isIElementPhotogrammetryScan(placeholder)) {
        // Photogrammetry scans show their own waypoints.
        return placeholder;
      } else if (placeholder && isIElementPanoInOdometryPath(placeholder)) {
        if (
          panoTargetRoomSection &&
          isIElementRoomSection(panoTargetRoomSection)
        ) {
          return panoTargetSection ?? panoTargetRoomSection;
        }
        return placeholder;
      }
      return placeholderSection;
    },
  ),
);

/**
 * @param placeholder - waypoint placeholder
 * @returns element to identify waypoint name for different types of scans (E57, Focus scan, Orbis)
 */
export function useWaypointReference(
  placeholder: IElementWaypoint | undefined,
): IElement | undefined {
  return useAppSelector(selectWaypointReference(placeholder));
}

/** Add an offset to the popper to position it just below the placeholder. */
export const POPPER_MODIFIERS = [
  {
    name: "offset",
    options: {
      offset: [0, POPPER_OFFSET],
    },
  },
];

type PlaceholderPreviewProps = {
  /** The placeholder Img360 or Img2d which is currently being hovered */
  placeholder?: IElementImg360 | IElementImg2d;

  /** Position at which the placeholder preview should appear */
  position?: Vector3;

  /**
   * Called when the pointer enters or leaves the preview popover itself.
   * Allows the parent to coordinate delayed hover cleanup.
   */
  onPreviewHoverChange?(isHovered: boolean): void;
};

/**
 * @returns  A dialog that displays a preview of an Img360 placeholder on hover,
 * while handling warm-up and cool-down transitions. The warm-up transition
 * introduces a delay before showing the preview window upon hovering,
 * ensuring a smooth and deliberate interaction. The cool-down transition
 * introduces a delay before hiding the preview window after moving the
 * pointer away, preventing flickering or abrupt disappearance.
 *
 * These transitions contribute to a better user experience by providing a
 * subtle delay for previewing and dismissing the content, reducing visual
 * noise and creating a more polished interaction.
 */
export function PlaceholderPreview({
  placeholder,
  position: customPosition,
  onPreviewHoverChange,
}: PlaceholderPreviewProps): JSX.Element | null {
  const theme = useTheme();
  /**
   * The duration used for both fade in/out of window as well as the warm up and cool down time to make the window visible/invisible
   * i.e handles the delay between pointer hover and the popup visibility both while hovering in (warm-up) and hovering out (cool-down)
   */
  const transitionDuration = theme.transitions.duration.shorter;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  const isPreviewHoveredRef = useRef(false);
  const [cachedPlaceholder, setCachedPlaceholder] = useState<
    IElementImg360 | IElementImg2d
  >();
  const [cachedPosition, setCachedPosition] = useState<
    Vector3 | Vector3Tuple
  >();

  const usesExternalPositionRef = useRef(false);

  const referenceElement = useWaypointReference(cachedPlaceholder);
  const parentDataset = useAppSelector(
    selectAncestor(referenceElement, isIElementGenericDataset),
  );

  const timeSeries = useAppSelector(
    selectAncestor(cachedPlaceholder, isIElementTimeSeriesRoom),
  );

  // If the current 360 belongs to a time series, we want
  // to use the room position and not the 360 one
  const location = timeSeries ?? cachedPlaceholder;
  const position = useAppSelector(
    (state) =>
      // use the floor position to avoid the placeholder being placed out of view in (video mode) paths with large vertical components
      location ? selectIElementFloorPosition(location)(state) : undefined,
    isEqual,
  );

  const placeholderPosition = useAppSelector(
    selectIElementWorldPosition(cachedPlaceholder?.id),
  );

  useEffect(() => {
    // Use the custom position if provided, otherwise use the position from the store
    if (customPosition) {
      usesExternalPositionRef.current = true;
    }
    const nextPosition = usesExternalPositionRef.current
      ? customPosition
      : position;

    // Keep the last known position while transitioning from marker hover to
    // popover hover. Cleanup clears this after the delayed close timeout.
    if (nextPosition) {
      setCachedPosition(nextPosition);
    }
  }, [customPosition, position]);

  const onPointerInDebounced = useRef<ReturnType<typeof setTimeout>>(undefined);
  const onPointerIn = useCallback(() => {
    clearTimeout(onPointerOutDebounced.current);
    clearTimeout(onPointerInDebounced.current);

    onPointerInDebounced.current = setTimeout(() => {
      setIsHovering(true);
      setCachedPlaceholder((prev) => placeholder ?? prev);
    }, transitionDuration);
  }, [placeholder, transitionDuration]);

  const onPointerOutDebounced =
    useRef<ReturnType<typeof setTimeout>>(undefined);

  const clearHoverPreview = useCallback(() => {
    setIsHovering(false);
    setCachedPlaceholder(undefined);
    setCachedPosition(undefined);
  }, []);

  const onPointerOut = useCallback(() => {
    clearTimeout(onPointerOutDebounced.current);

    onPointerOutDebounced.current = setTimeout(() => {
      // Keep preview alive if the pointer reached the popover before timeout.
      if (isPreviewHoveredRef.current) {
        return;
      }
      clearHoverPreview();
    }, PREVIEW_CLOSE_DELAY_MS);
  }, [clearHoverPreview]);

  useEffect(() => {
    if (placeholder || isPreviewHovered) {
      onPointerIn();
    } else if (cachedPlaceholder) {
      onPointerOut();
    } else {
      // The user has hovered over the placeholder too quickly, so cancel the onPointerInDebounced
      clearTimeout(onPointerInDebounced.current);
    }
  }, [
    cachedPlaceholder,
    isPreviewHovered,
    onPointerIn,
    onPointerOut,
    placeholder,
  ]);

  // Prevent lingering timers if the component unmounts during a transition.
  useEffect(
    () => () => {
      clearTimeout(onPointerInDebounced.current);
      clearTimeout(onPointerOutDebounced.current);
    },
    [],
  );

  const handlePreviewHoverChange = useCallback(
    (isHovered: boolean) => {
      isPreviewHoveredRef.current = isHovered;
      setIsPreviewHovered(isHovered);
      onPreviewHoverChange?.(isHovered);

      if (isHovered) {
        clearTimeout(onPointerOutDebounced.current);
        setIsHovering(true);
      }
    },
    [onPreviewHoverChange],
  );

  const imageUri = useImgPreviewSignedUrl(cachedPlaceholder);

  const cachedObject = useCached3DObjectIfReady(
    cachedPlaceholder,
    "PlaceholderPreview",
  );
  const validCachedObject = cachedObject instanceof Error ? null : cachedObject;
  const panoObject = isPanoObject(validCachedObject) ? validCachedObject : null;

  const scanDate = useScanDate(placeholder);

  const dispatch = useAppDispatch();
  const openCapture = useCallback(() => {
    if (!cachedPlaceholder) return;
    dispatch(setActiveElement(cachedPlaceholder.id));
    dispatch(setFlyWalkSceneFilter(SceneFilter.Pano));
    dispatch(changeMode("walk"));
  }, [cachedPlaceholder, dispatch]);
  const icon = useCaptureTreeIcon(cachedPlaceholder);
  const annotationIconColor = useAnnotationIconColor();
  const isCapturePreview = useAppSelector(
    selectHasFeature(Features.CaptureHoverPreview),
  );
  const { annotations, onAnnotationClick } = useCaptureAnnotationItems(
    isCapturePreview ? cachedPlaceholder : undefined,
    openCapture,
  );
  const aiDescription = useCaptureAiDescription(
    isCapturePreview ? cachedPlaceholder?.id : undefined,
  );

  if (!cachedPlaceholder || !cachedPosition || !referenceElement) return null;

  return (
    <AppAwareHtml
      position={cachedPosition}
      calculatePosition={roundedCalculatePosition}
      style={{
        backdropFilter: "blur(4px) brightness(40%)",
        borderRadius: 6,
      }}
    >
      {/* anchor element where the popper attaches to */}
      <Box component="div" ref={setAnchorEl} />
      {anchorEl && (
        <PlaceholderPreviewBase
          name={referenceElement.name}
          imageUri={imageUri}
          panoElement={
            isIElementImg360(cachedPlaceholder) ? cachedPlaceholder : undefined
          }
          hasPanoramaMedia={!!panoObject}
          createdAt={scanDate}
          isVisible={isHovering}
          onPreviewHoverChange={handlePreviewHoverChange}
          disablePortal={false}
          anchorEl={anchorEl}
          datasetName={parentDataset?.name}
          datasetType={parentDataset?.typeHint}
          coordinates={
            parentDataset || isCapturePreview ? placeholderPosition : undefined
          }
          popperModifiers={POPPER_MODIFIERS}
          annotations={annotations}
          onAnnotationClick={onAnnotationClick}
          isCapturePreview={isCapturePreview}
          aiDescription={aiDescription}
          aiDescriptionImageId={cachedPlaceholder.id}
          icon={icon}
          annotationIconColor={annotationIconColor}
        />
      )}
    </AppAwareHtml>
  );
}
